import React, { useMemo, useState } from 'react';
import { ContentCopy, CheckCircle } from '@mui/icons-material';
import { getAuthUrl, getPaymentUrl } from '../../data/apiConfig';

const SNIPPETS = {
  curl: `# 1) Obtenir un token OAuth2 (client_credentials)
curl -X POST "${getAuthUrl()}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Basic base64(client_id:client_secret)" \\
  -d '{"grant_type":"client_credentials"}'`,
  javascript: `// Node.js (axios) - créer une demande de paiement
const axios = require('axios');

const res = await axios.post(
  '${getPaymentUrl()}',
  {
    amount: 100.5,
    currency: 'USD',
    ref: 'CMD-2024-001',
    referer_url: 'https://votre-site.com/confirmation',
  },
  {
    headers: {
      Authorization: 'Bearer <access_token>',
      'Content-Type': 'application/json',
    },
  }
);

console.log(res.data);`,
  python: `# Python (requests) - créer une demande de paiement
import requests

res = requests.post(
  "${getPaymentUrl()}",
  json={
    "amount": 100.5,
    "currency": "USD",
    "ref": "CMD-2024-001",
    "referer_url": "https://votre-site.com/confirmation",
  },
  headers={
    "Authorization": "Bearer <access_token>",
    "Content-Type": "application/json",
  },
)

print(res.json())`,
};

const QuickStartSnippetSection = () => {
  const [active, setActive] = useState('curl');
  const [copied, setCopied] = useState(false);

  const snippet = useMemo(() => SNIPPETS[active] || SNIPPETS.curl, [active]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <section className="py-16 bg-white dark:bg-slate-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Exemple rapide (copier / coller)</h2>
            <p className="text-gray-600 mt-2">
              Un chemin court vers votre premier appel API : token OAuth2, puis création de demande de paiement.
            </p>
          </div>
          <button
            onClick={copyToClipboard}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {copied ? <CheckCircle className="text-green-600" /> : <ContentCopy className="text-gray-500" />}
            <span className="font-medium">{copied ? 'Copié' : 'Copier'}</span>
          </button>
        </div>

        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 bg-white">
            {[
              { id: 'curl', label: 'cURL' },
              { id: 'javascript', label: 'JavaScript' },
              { id: 'python', label: 'Python' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  active === t.id ? 'bg-amber-100 text-amber-800' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <pre className="bg-gray-900 text-green-400 text-sm overflow-x-auto p-6">
            <code>{snippet}</code>
          </pre>
        </div>
      </div>
    </section>
  );
};

export default QuickStartSnippetSection;

